/**
 * BrainFlow — Flow controller for Brain & Clarity healing
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import HealingEntry from './HealingEntry';
import GenericArrival from './GenericArrival';
import PositioningGuide from './PositioningGuide';
import GenericAffirmationsPreview from './GenericAffirmationsPreview';
import GenericImmersion from './GenericImmersion';
import HealingCompletion from './HealingCompletion';
import BrainVisualEngine from './BrainVisualEngine';
import useAudioMode from '../../hooks/useAudioMode';

const CONDITION = 'brain-clarity';
const CONDITION_LABEL = 'Brain & Clarity';

const ARRIVAL_TEXTS = [
  'Take a moment\u2026',
  'Let everything slow\u2026',
  'Allow your mind\nto simply be here',
  'You are here\u2026\nand clarity is already arriving',
];

const CATEGORY_META = {
  calming_clarity: { label: 'Calming into Clarity', description: 'Stillness beneath the noise' },
  releasing_overthinking: { label: 'Releasing Overthinking', description: 'Letting mental loops dissolve' },
  expansion_openness: { label: 'Expanding Awareness', description: 'Spacious, open perspective' },
  integration_wisdom: { label: 'Deeper Knowing', description: 'Wisdom beyond thought' },
  peace_stillness: { label: 'Stillness', description: 'Calm, clear, at peace' },
};
const CATEGORY_ORDER = ['calming_clarity', 'releasing_overthinking', 'expansion_openness', 'integration_wisdom', 'peace_stillness'];

const TEXT_SYNC = [
  { t: 3, text: 'softly\u2026', dur: 3000 },
  { t: 10, text: 'still\u2026', dur: 3000 },
  { t: 20, text: 'a quiet space\u2026', dur: 3500 },
  { t: 35, text: 'letting go\u2026', dur: 3000 },
  { t: 50, text: 'settling\u2026', dur: 3000 },
  { t: 68, text: 'clearing\u2026', dur: 3000 },
  { t: 85, text: 'spacious\u2026', dur: 3000 },
  { t: 105, text: 'open\u2026', dur: 3000 },
  { t: 130, text: 'expanding\u2026', dur: 3000 },
  { t: 160, text: 'effortless\u2026', dur: 3000 },
  { t: 190, text: 'clear\u2026', dur: 3000 },
  { t: 220, text: 'knowing\u2026', dur: 3000 },
  { t: 255, text: 'peaceful\u2026', dur: 3000 },
  { t: 285, text: 'deeply calm\u2026', dur: 3500 },
  { t: 315, text: 'this clarity is yours\u2026', dur: 4000 },
  { t: 345, text: 'this stillness remains\u2026', dur: 3500 },
  { t: 375, text: 'whole\u2026', dur: 3000 },
  { t: 395, text: 'rest\u2026', dur: 5000 },
];

const BrainFlow = () => {
  const navigate = useNavigate();
  const [, setMode] = useAudioMode();
  const [step, setStep] = useState(0);
  const [affirmations, setAffirmations] = useState([]);

  return (
    <div className="fixed inset-0 bg-black" data-testid="brain-flow">
      {step === 0 && (
        <HealingEntry title={CONDITION_LABEL}
          subtitle="A quiet clarity begins to settle…"
          description="spaciousness opening within the mind"
          onBegin={(mode) => { setMode(mode); setStep(3); }}
          onBack={() => navigate('/healing')}
          area="healing_brain"
          glow="rgba(160,200,235,0.55)"
          accent="rgba(183,210,212,0.6)" />
      )}
      {step === 1 && <GenericArrival texts={ARRIVAL_TEXTS} glowColor="rgba(180,195,220,0.25)" onContinue={() => setStep(2)} />}
      {step === 2 && <PositioningGuide onReady={() => setStep(3)} />}
      {step === 3 && (
        <GenericAffirmationsPreview condition={CONDITION} categoryMeta={CATEGORY_META} categoryOrder={CATEGORY_ORDER}
          title="Mind Affirmations" onContinue={(affs) => { setAffirmations(affs); setStep(4); }} />
      )}
      {step === 4 && (
        <GenericImmersion VisualEngine={BrainVisualEngine} textSync={TEXT_SYNC}
          affirmations={affirmations} title="Brain Clarity" onComplete={() => setStep(5)} />
      )}
      {step === 5 && (
        <HealingCompletion onSave={() => {}} onRepeat={() => setStep(0)}
          onHome={() => navigate('/healing')} homeLabel="Return to Healing" />
      )}
    </div>
  );
};

export default BrainFlow;
